import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FileWarning,
  ChevronDown,
  ChevronRight,
  Paperclip,
  Video,
  Eye,
  Settings,
  GripVertical,
  Info,
} from "lucide-react";
import { DiscrepancyService } from "../../services/discrepancyService";
import DiscrepancyModal from "./DiscrepancyModal";
import DiscrepancyReviewModal from "./DiscrepancyReviewModal";
import DiscrepancySearch from "./DiscrepancySearch";
import LoadingSpinner from "../LoadingSpinner";
import ErrorMessage from "../ErrorMessage";
import ContactDetailsModal from "../ContactDetailsModal";
import { Table, Button, Popover, Checkbox } from "../ui";
import { useRealtimeDiscrepancies } from "../../hooks/useRealtimeDiscrepancies";
import { parseCurrentUrl, createNavigationHelper } from "../../utils/urlManager";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Column Item Component
const SortableColumnItem = ({ column, checked, onChange }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2)',
        backgroundColor: isDragging ? 'var(--dark-elevated)' : 'transparent',
        borderRadius: 'var(--radius-md)',
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
    >
      <div
        {...attributes}
        {...listeners}
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          color: 'var(--gray-400)',
          padding: '4px',
        }}
      >
        <GripVertical size={16} />
      </div>
      <Checkbox
        label={column.label}
        checked={checked}
        onChange={onChange}
      />
    </div>
  );
};

// Status order and colors configuration (constant)
const statusConfig = {
  Draft: { order: 1, color: "#6b7280", expanded: true },
  "Waiting to Review": { order: 2, color: "#10b981", expanded: true },
  Sent: { order: 3, color: "#3b82f6", expanded: false },
  "Reviewed - not sent": { order: 4, color: "#f59e0b", expanded: false },
  Resolved: { order: 5, color: "#14b8a6", expanded: false },
  // Note: Removed "Create Email Draft" - not a real status
  Open: { order: 7, color: "#ef4444", expanded: true },
  "In Progress": { order: 8, color: "#f59e0b", expanded: false },
  "Under Review": { order: 9, color: "#f59e0b", expanded: false },
  Closed: { order: 10, color: "#6b7280", expanded: false },
  Cancelled: { order: 11, color: "#6b7280", expanded: false },
};

/**
 * DiscrepancyTable
 *
 * Standardized discrepancy board using the UI component library with grouped/collapsible structure.
 *
 * Features:
 * - Grouped by status with collapsible sections
 * - Status-based color coding
 * - Group-specific sorting (Sent by date, others by job number)
 * - Search functionality with dropdown
 * - Table component for each group
 * - Auto-resize columns
 * - Real-time updates
 * - Modal integration for create/edit
 */
const DiscrepancyTable = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Parse URL to get discrepancy ID
  const { id: urlDiscrepancyId } = parseCurrentUrl(location.pathname);

  const [discrepancies, setDiscrepancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDiscrepancy, setSelectedDiscrepancy] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [autoResizeEnabled, setAutoResizeEnabled] = useState(true);
  const [showColumnManager, setShowColumnManager] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);

  // Column visibility and ordering state
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('discrepancy-visible-columns');
    const defaultColumns = {
      expand: true,
      project: true,
      type: true,
      jobNumber: true,
      client: true,
      contact: true,
      modelingStatus: true,
      modelingState: true,
      status: true,
      markup: true,
      loom: true,
      createdBy: true,
      createdDate: true,
      sentDate: true,
    };

    if (saved) {
      const savedColumns = JSON.parse(saved);
      // Merge with defaults to add any new columns
      return { ...defaultColumns, ...savedColumns };
    }

    return defaultColumns;
  });
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem('discrepancy-column-order');
    return saved ? JSON.parse(saved) : [];
  });

  // Track when we're intentionally closing to prevent re-opening
  const isClosingRef = useRef(false);

  // Navigation helper
  const navigationHelper = createNavigationHelper(navigate);

  // Expanded groups state
  const [expandedGroups, setExpandedGroups] = useState({
    "Waiting to Review": true,
    "Reviewed - not sent": false,
    Sent: false,
    Resolved: false,
    Draft: true,
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id);
      const newIndex = columnOrder.indexOf(over.id);

      const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
      setColumnOrder(newOrder);
      localStorage.setItem('discrepancy-column-order', JSON.stringify(newOrder));
    }
  };

  // Load discrepancies on mount
  const loadDiscrepancies = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await DiscrepancyService.getAllDiscrepancies();
      setDiscrepancies(data);
    } catch (err) {
      console.error("Error loading discrepancies:", err);
      setError(err.message || "Failed to load discrepancies");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDiscrepancies();
  }, [loadDiscrepancies]);

  // Real-time discrepancy handlers
  const handleDiscrepancyUpdate = useCallback((updatedDiscrepancy) => {
    console.log('🔴 Handling real-time discrepancy update:', updatedDiscrepancy);
    setDiscrepancies(prev =>
      prev.map(disc =>
        disc.id === updatedDiscrepancy.id ? updatedDiscrepancy : disc
      )
    );
  }, []);

  const handleDiscrepancyInsert = useCallback((newDiscrepancy) => {
    console.log('🔴 Handling real-time discrepancy insert:', newDiscrepancy);
    setDiscrepancies(prev => [newDiscrepancy, ...prev]);
  }, []);

  const handleRealtimeDiscrepancyDelete = useCallback((deletedDiscrepancyId) => {
    console.log('🔴 Handling real-time discrepancy delete:', deletedDiscrepancyId);
    setDiscrepancies(prev =>
      prev.filter(disc => disc.id !== deletedDiscrepancyId)
    );
  }, []);

  // Set up real-time subscription for discrepancies (always-on)
  useRealtimeDiscrepancies(
    handleDiscrepancyUpdate,
    handleDiscrepancyInsert,
    handleRealtimeDiscrepancyDelete,
    true
  );

  // Group discrepancies by status
  const groupedDiscrepancies = useMemo(() => {
    const groups = {};

    discrepancies.forEach((disc) => {
      const status = disc.discrepancyStatus || "Unknown";
      if (!groups[status]) {
        groups[status] = [];
      }
      groups[status].push(disc);
    });

    // Sort each group's items
    Object.keys(groups).forEach((key) => {
      if (key === "Sent") {
        // Sort "Sent" group by sent date (most recent first)
        groups[key].sort((a, b) => {
          const aDate = a.sentDate ? new Date(a.sentDate) : new Date(0);
          const bDate = b.sentDate ? new Date(b.sentDate) : new Date(0);
          return bDate - aDate; // Most recent first
        });
      } else {
        // Sort other groups by job number (highest to lowest)
        groups[key].sort((a, b) => {
          const aJobNumber = a.project?.jobNumber || "";
          const bJobNumber = b.project?.jobNumber || "";

          // Parse job numbers (format: YYYY-NNN)
          const parseJobNumber = (jobNum) => {
            if (!jobNum) return 0;
            const parts = jobNum.split("-");
            if (parts.length === 2) {
              const year = parseInt(parts[0]) || 0;
              const num = parseInt(parts[1]) || 0;
              return year * 1000 + num; // Combine year and number for sorting
            }
            // Fallback for other formats - try to extract any number
            const match = jobNum.match(/\d+/);
            return match ? parseInt(match[0]) : 0;
          };

          const aValue = parseJobNumber(aJobNumber);
          const bValue = parseJobNumber(bJobNumber);

          // Sort in descending order (highest to lowest)
          return bValue - aValue;
        });
      }
    });

    // Sort groups by predefined order
    const sortedGroups = {};
    Object.keys(groups)
      .sort((a, b) => {
        const orderA = statusConfig[a]?.order || 999;
        const orderB = statusConfig[b]?.order || 999;
        return orderA - orderB;
      })
      .forEach((key) => {
        sortedGroups[key] = groups[key];
      });

    return sortedGroups;
  }, [discrepancies]);

  // Toggle group expansion
  const toggleGroup = (status) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [status]: !prev[status],
    }));
  };

  // Handle row click to open modal
  const handleRowClick = useCallback((discrepancy) => {
    console.log("Opening modal with discrepancy:", discrepancy);
    console.log("Discrepancy projectId:", discrepancy.projectId);

    // Navigate to discrepancy URL
    navigationHelper.goToDiscrepancy(discrepancy.id);

    setSelectedDiscrepancy(discrepancy);
    setIsCreating(false);
    setModalOpen(true);
  }, [navigationHelper]);

  // Handle auto-opening discrepancy from URL
  useEffect(() => {
    // Don't re-open modal if we're in the process of closing it
    if (isClosingRef.current) {
      return;
    }

    // Check for URL-based discrepancy ID first
    if (urlDiscrepancyId && discrepancies.length > 0) {
      const discrepancy = discrepancies.find(d => d.id === parseInt(urlDiscrepancyId));
      if (discrepancy && !modalOpen) {
        setSelectedDiscrepancy(discrepancy);
        setIsCreating(false);
        setModalOpen(true);
      }
    }

    // Legacy support: check localStorage for old method
    const openDiscrepancyId = localStorage.getItem('openDiscrepancyId');
    if (openDiscrepancyId && discrepancies.length > 0 && !urlDiscrepancyId) {
      const discrepancy = discrepancies.find(d => d.id === parseInt(openDiscrepancyId));
      if (discrepancy) {
        // Use the new URL-based method
        navigationHelper.goToDiscrepancy(discrepancy.id);
        localStorage.removeItem('openDiscrepancyId'); // Clear after migrating
      }
    }
  }, [urlDiscrepancyId, discrepancies, modalOpen, navigationHelper]);

  // Handle create new discrepancy
  const handleCreateNew = useCallback(() => {
    setSelectedDiscrepancy(null);
    setIsCreating(true);
    setModalOpen(true);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    // Set closing flag to prevent re-opening
    isClosingRef.current = true;

    // Close modal immediately
    setModalOpen(false);
    setSelectedDiscrepancy(null);
    setIsCreating(false);

    // Navigate back to discrepancies list
    navigationHelper.goToDiscrepancy();

    // Reset closing flag after navigation
    setTimeout(() => {
      isClosingRef.current = false;
    }, 100);
  }, [navigationHelper]);

  // Handle discrepancy save (create or update)
  const handleDiscrepancySave = useCallback(
    async (discrepancyData, closeModal = true, isAutoSave = false) => {
      try {
        let savedDiscrepancy;

        if (isCreating) {
          savedDiscrepancy =
            await DiscrepancyService.createDiscrepancy(discrepancyData, window.location.origin);
          setDiscrepancies((prev) => [savedDiscrepancy, ...prev]);
          // Close modal only after creating new discrepancy
          if (closeModal) {
            handleModalClose();
          }
        } else if (selectedDiscrepancy) {
          // For email send operations, the database was already updated by the send service
          // Just update the local state directly to avoid redundant database calls
          // Only skip database update when status is changing TO 'Sent' AND sentDate is being set (email send operation)
          if (discrepancyData.discrepancyStatus === 'Sent' && discrepancyData.sentDate) {
            // Email send operation - create a saved discrepancy object with the new status
            savedDiscrepancy = {
              ...selectedDiscrepancy,
              ...discrepancyData,
              status: discrepancyData.discrepancyStatus,
              sent_date: discrepancyData.sentDate || new Date().toISOString()
            };

            // Update local state only
            setDiscrepancies((prev) =>
              prev.map((disc) =>
                disc.id === selectedDiscrepancy.id ? savedDiscrepancy : disc,
              ),
            );
          } else {
            // Regular save operation - update database and local state
            // This includes all status changes except email send operations
            savedDiscrepancy = await DiscrepancyService.updateDiscrepancy(
              selectedDiscrepancy.id,
              discrepancyData,
            );
            setDiscrepancies((prev) =>
              prev.map((disc) =>
                disc.id === savedDiscrepancy.id ? savedDiscrepancy : disc,
              ),
            );
          }

          // Don't update selectedDiscrepancy during auto-save to prevent form reset
          if (!isAutoSave) {
            // Update the selectedDiscrepancy with the new data only for manual saves
            setSelectedDiscrepancy(savedDiscrepancy);
          }

          // Don't close modal after updating - keep it open
          if (closeModal) {
            handleModalClose();
          }
        }
      } catch (err) {
        console.error("Error saving discrepancy:", err);
        throw err; // Let modal handle the error
      }
    },
    [isCreating, selectedDiscrepancy, handleModalClose],
  );

  // Handle discrepancy delete
  const handleDiscrepancyDelete = useCallback(
    async (discrepancyId) => {
      if (
        !window.confirm("Are you sure you want to delete this discrepancy?")
      ) {
        return;
      }

      try {
        await DiscrepancyService.deleteDiscrepancy(discrepancyId);
        setDiscrepancies((prev) =>
          prev.filter((disc) => disc.id !== discrepancyId),
        );
        handleModalClose();
      } catch (err) {
        console.error("Error deleting discrepancy:", err);
        alert("Failed to delete discrepancy: " + err.message);
      }
    },
    [handleModalClose],
  );

  // Handle manual column resize - auto-disable auto-resize
  const handleManualResize = () => {
    if (autoResizeEnabled) {
      setAutoResizeEnabled(false);
    }
  };

  // Handle contact info click
  const handleContactInfoClick = useCallback((e, contact) => {
    e.stopPropagation();
    setSelectedContact(contact);
    setShowContactModal(true);
  }, []);

  // Get status color
  const getStatusColor = (status) => {
    return statusConfig[status]?.color || "#6b7280";
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${month}/${day}/${year}`;
  };

  // Define all columns for the Table component
  const allColumns = useMemo(() => [
    {
      id: 'expand',
      label: '',
      field: 'id',
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      sortable: false,
      resizable: false,
      align: 'center',
      contentType: 'icon',
      render: (value, row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(row);
          }}
          style={{
            padding: '8px',
            background: 'none',
            border: 'none',
            color: 'var(--gray-400)',
            cursor: 'pointer',
            borderRadius: '8px',
            transition: 'background-color 0.2s, color 0.2s, transform 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            minWidth: '36px',
            minHeight: '36px',
            maxWidth: '36px',
            maxHeight: '36px',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--gray-800)';
            e.currentTarget.style.color = 'var(--cta-blue)';
            e.currentTarget.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--gray-400)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
          title="View discrepancy details"
          aria-label={`View details for discrepancy in ${row.project?.projectName || 'project'}`}
        >
          <Eye size={20} />
        </button>
      ),
    },
    {
      id: 'project',
      label: 'Project',
      field: 'project',
      width: 200,
      minWidth: 150,
      maxWidth: 400,
      sortable: false,
      resizable: true,
      contentType: 'text',
      formatValue: (value) => value?.projectName || '—',
      render: (value) => value?.projectName || '—',
    },
    {
      id: 'type',
      label: 'Items',
      field: 'discrepancyType',
      width: 200,
      minWidth: 150,
      maxWidth: 400,
      sortable: false,
      resizable: true,
      contentType: 'text',
      formatValue: (value) => value || 'Unknown Type',
      render: (value) => (
        <div style={{ fontWeight: 'var(--font-medium)' }}>
          {value || 'Unknown Type'}
        </div>
      ),
    },
    {
      id: 'jobNumber',
      label: 'Job Number',
      field: 'project',
      width: 120,
      minWidth: 100,
      maxWidth: 200,
      sortable: false,
      resizable: true,
      contentType: 'text',
      formatValue: (value) => {
        const jobNumber = value?.jobNumber || "";
        const rev = value?.rev;
        return rev && parseInt(rev) > 0 ? `${jobNumber} Rev${rev}` : jobNumber || "—";
      },
      render: (value) => {
        const jobNumber = value?.jobNumber || "";
        const rev = value?.rev;
        return rev && parseInt(rev) > 0 ? `${jobNumber} Rev${rev}` : jobNumber || "—";
      },
    },
    {
      id: 'client',
      label: 'Client',
      field: 'project',
      width: 150,
      minWidth: 120,
      maxWidth: 300,
      sortable: false,
      resizable: true,
      contentType: 'text',
      formatValue: (value) => value?.client?.name || '—',
      render: (value) => value?.client?.name || '—',
    },
    {
      id: 'contact',
      label: 'Contact',
      field: 'project',
      width: 180,
      minWidth: 150,
      maxWidth: 300,
      sortable: false,
      resizable: true,
      contentType: 'text',
      formatValue: (value) => value?.contacts?.name || value?.contact?.name || '—',
      render: (value, row) => {
        const contact = value?.contacts || value?.contact;

        if (!contact) return '—';

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleContactInfoClick(e, contact);
              }}
              style={{
                padding: '8px',
                background: 'none',
                border: 'none',
                color: 'var(--gray-400)',
                cursor: 'pointer',
                borderRadius: '8px',
                transition: 'background-color 0.2s, color 0.2s, transform 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px',
                minWidth: '36px',
                minHeight: '36px',
                maxWidth: '36px',
                maxHeight: '36px',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--gray-800)';
                e.currentTarget.style.color = 'var(--cta-blue)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = 'var(--gray-400)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title="View contact details"
              aria-label={`View details for ${contact.name}`}
            >
              <Info size={20} />
            </button>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {contact.name}
            </span>
          </div>
        );
      },
    },
    {
      id: 'modelingStatus',
      label: 'Modeling Status',
      field: 'project',
      width: 140,
      minWidth: 120,
      maxWidth: 250,
      sortable: false,
      resizable: true,
      contentType: 'text',
      formatValue: (value) => value?.modelingStatus || '—',
      render: (value) => value?.modelingStatus || '—',
    },
    {
      id: 'modelingState',
      label: 'Modeling State',
      field: 'project',
      width: 140,
      minWidth: 120,
      maxWidth: 250,
      sortable: false,
      resizable: true,
      contentType: 'text',
      formatValue: (value) => value?.modelingState || '—',
      render: (value) => value?.modelingState || '—',
    },
    {
      id: 'status',
      label: 'Status',
      field: 'discrepancyStatus',
      width: 120,
      minWidth: 100,
      maxWidth: 200,
      sortable: false,
      resizable: true,
      align: 'center',
      contentType: 'badge',
      formatValue: (value) => value || 'Open',
      render: (value) => (
        <span
          style={{
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600',
            color: 'white',
            textTransform: 'capitalize',
            backgroundColor: getStatusColor(value),
          }}
        >
          {value || 'Open'}
        </span>
      ),
    },
    {
      id: 'markup',
      label: 'Files',
      field: 'markupAttachment',
      width: 70,
      minWidth: 70,
      maxWidth: 70,
      sortable: false,
      resizable: false,
      align: 'center',
      contentType: 'icon',
      render: (value) => {
        if (!value || !Array.isArray(value) || value.length === 0) return null;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
            <Paperclip
              size={16}
              style={{ color: 'var(--cta-blue)' }}
              title={`${value.length} file(s) attached`}
            />
            <span
              style={{
                fontSize: '12px',
                fontWeight: '600',
                color: 'var(--gray-400)',
                background: 'var(--dark-elevated)',
                padding: '2px 6px',
                borderRadius: '10px',
                minWidth: '20px',
                textAlign: 'center',
              }}
            >
              {value.length}
            </span>
          </div>
        );
      },
    },
    {
      id: 'loom',
      label: 'Loom',
      field: 'loomLink',
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      sortable: false,
      resizable: false,
      align: 'center',
      contentType: 'icon',
      render: (value) => {
        if (!value) return null;
        return (
          <a
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ color: 'var(--cta-blue)', display: 'flex', justifyContent: 'center' }}
          >
            <Video size={16} />
          </a>
        );
      },
    },
    {
      id: 'createdBy',
      label: 'Created By',
      field: 'createdByName',
      width: 120,
      minWidth: 100,
      maxWidth: 250,
      sortable: false,
      resizable: true,
      contentType: 'text',
      formatValue: (value, row) => value || row?.createdBy || '—',
      render: (value, row) => value || row.createdBy || '—',
    },
    {
      id: 'createdDate',
      label: 'Created Date',
      field: 'createdAt',
      width: 110,
      minWidth: 90,
      maxWidth: 150,
      sortable: false,
      resizable: true,
      contentType: 'text',
      formatValue: (value) => formatDate(value),
      render: (value) => formatDate(value),
    },
    {
      id: 'sentDate',
      label: 'Sent Date',
      field: 'sentDate',
      width: 100,
      minWidth: 90,
      maxWidth: 150,
      sortable: false,
      resizable: true,
      contentType: 'text',
      formatValue: (value) => formatDate(value),
      render: (value) => formatDate(value),
    },
  ], [handleRowClick, handleContactInfoClick]); // handleRowClick and handleContactInfoClick are used in column renders

  // Initialize column order from allColumns if not set, or migrate to add new columns
  useEffect(() => {
    if (allColumns.length === 0) return;

    const allColumnIds = allColumns.map(col => col.id);

    if (columnOrder.length === 0) {
      // No saved order - use default
      setColumnOrder(allColumnIds);
      localStorage.setItem('discrepancy-column-order', JSON.stringify(allColumnIds));
    } else {
      // Check if any new columns need to be added
      const missingColumns = allColumnIds.filter(id => !columnOrder.includes(id));

      if (missingColumns.length > 0) {
        // Add new columns after existing ones (maintaining relative order from allColumns)
        const updatedOrder = [
          ...columnOrder.filter(id => allColumnIds.includes(id)), // Keep existing valid columns
          ...missingColumns // Add missing columns at the end
        ];
        setColumnOrder(updatedOrder);
        localStorage.setItem('discrepancy-column-order', JSON.stringify(updatedOrder));
      }
    }
  }, [columnOrder, allColumns]);

  // Apply column visibility and order
  const columns = useMemo(() => {
    if (columnOrder.length === 0) {
      return allColumns;
    }

    const orderedColumns = columnOrder
      .filter(id => visibleColumns[id])
      .map(id => allColumns.find(col => col.id === id))
      .filter(col => col !== undefined);

    return orderedColumns;
  }, [allColumns, columnOrder, visibleColumns]);

  // Determine which group to use for auto-resize
  // Priority: Only auto-resize on "Draft" group, but size based on Draft + Waiting to Review combined data
  const autoResizeGroup = useMemo(() => {
    const waitingToReviewItems = groupedDiscrepancies["Waiting to Review"];
    const draftItems = groupedDiscrepancies["Draft"];

    // Only auto-resize when Draft group renders, to avoid duplicate calculations
    // But the Draft group will size columns based on both Draft + Waiting to Review data
    if (draftItems && draftItems.length > 0) {
      return "Draft";
    } else if (waitingToReviewItems && waitingToReviewItems.length > 0) {
      return "Waiting to Review";
    }
    return null; // No auto-resize if neither group has items
  }, [groupedDiscrepancies]);

  // Combined data for auto-resize calculation (Draft + Waiting to Review)
  const autoResizeData = useMemo(() => {
    const draftItems = groupedDiscrepancies["Draft"] || [];
    const waitingItems = groupedDiscrepancies["Waiting to Review"] || [];
    return [...draftItems, ...waitingItems];
  }, [groupedDiscrepancies]);

  if (loading) {
    return <LoadingSpinner message="Loading discrepancies..." />;
  }

  return (
    <div style={{ padding: '24px', backgroundColor: 'var(--dark-bg)', minHeight: '100vh' }}>
      {/* Error Message */}
      {error && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <ErrorMessage
            message={error}
            onRetry={loadDiscrepancies}
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      {/* Page Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-6)',
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 'var(--text-3xl)',
              fontWeight: 'var(--font-bold)',
              color: 'var(--gray-50)',
            }}
          >
            Discrepancies
          </h1>
          <p
            style={{
              margin: 0,
              marginTop: 'var(--space-2)',
              fontSize: 'var(--text-sm)',
              color: 'var(--gray-400)',
            }}
          >
            {discrepancies.length} total discrepancies
            {Object.keys(groupedDiscrepancies).length > 0 &&
              ` • ${Object.entries(groupedDiscrepancies).map(([status, items]) =>
                `${items.length} ${status}`
              ).join(', ')}`
            }
          </p>
        </div>

        <Button
          variant="primary"
          size="md"
          onClick={handleCreateNew}
        >
          + New Discrepancy
        </Button>
      </div>

      {/* Table with integrated toolbar */}
      <div style={{
        border: '1px solid var(--gray-600)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '90vh',
      }}>
        {/* Search Bar and Auto-resize Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-4)',
            backgroundColor: 'var(--dark-bg)',
            borderBottom: '1px solid var(--gray-600)',
            gap: 'var(--space-4)',
            flexShrink: 0,
          }}
        >
          <div style={{ width: '300px', flexShrink: 0 }}>
            <DiscrepancySearch
              discrepancies={discrepancies}
              onDiscrepancySelect={handleRowClick}
              getStatusColor={getStatusColor}
            />
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            {/* Column Visibility/Ordering Popover */}
            <Popover
              isOpen={showColumnManager}
              onClose={() => setShowColumnManager(false)}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowColumnManager(!showColumnManager)}
                >
                  <Settings size={16} />
                  Columns
                </Button>
              }
              position="bottom-right"
            >
              <div style={{
                padding: 'var(--space-4)',
                minWidth: '280px',
                maxWidth: '320px',
              }}>
                <h3 style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-50)',
                }}>
                  Manage Columns
                </h3>

                <div style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  paddingRight: 'var(--space-2)'
                }}>
                  {/* Visible Columns (Checked) */}
                  <div style={{
                    marginBottom: 'var(--space-3)'
                  }}>
                    <div style={{
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-semibold)',
                      color: 'var(--gray-400)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 'var(--space-2)',
                      paddingLeft: 'var(--space-2)'
                    }}>
                      Visible Columns
                    </div>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={columnOrder.filter(id => id !== 'expand' && id !== 'status' && visibleColumns[id])}
                        strategy={verticalListSortingStrategy}
                      >
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 'var(--space-1)'
                        }}>
                          {columnOrder
                            .filter(id => id !== 'expand' && id !== 'status' && visibleColumns[id])
                            .map(colId => {
                              const col = allColumns.find(c => c.id === colId);
                              if (!col) return null;
                              return (
                                <SortableColumnItem
                                  key={col.id}
                                  column={col}
                                  checked={visibleColumns[col.id]}
                                  onChange={(e) => {
                                    const newVisibleColumns = {
                                      ...visibleColumns,
                                      [col.id]: e.target.checked
                                    };
                                    setVisibleColumns(newVisibleColumns);
                                    localStorage.setItem('discrepancy-visible-columns', JSON.stringify(newVisibleColumns));
                                  }}
                                />
                              );
                            })}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </div>

                  {/* Hidden Columns (Unchecked) */}
                  {columnOrder.filter(id => id !== 'expand' && id !== 'status' && !visibleColumns[id]).length > 0 && (
                    <div style={{
                      paddingTop: 'var(--space-3)',
                      borderTop: '1px solid var(--gray-700)'
                    }}>
                      <div style={{
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-semibold)',
                        color: 'var(--gray-400)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: 'var(--space-2)',
                        paddingLeft: 'var(--space-2)'
                      }}>
                        Hidden Columns
                      </div>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={columnOrder.filter(id => id !== 'expand' && id !== 'status' && !visibleColumns[id])}
                          strategy={verticalListSortingStrategy}
                        >
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--space-1)'
                          }}>
                            {columnOrder
                              .filter(id => id !== 'expand' && id !== 'status' && !visibleColumns[id])
                              .map(colId => {
                                const col = allColumns.find(c => c.id === colId);
                                if (!col) return null;
                                return (
                                  <SortableColumnItem
                                    key={col.id}
                                    column={col}
                                    checked={visibleColumns[col.id]}
                                    onChange={(e) => {
                                      const newVisibleColumns = {
                                        ...visibleColumns,
                                        [col.id]: e.target.checked
                                      };
                                      setVisibleColumns(newVisibleColumns);
                                      localStorage.setItem('discrepancy-visible-columns', JSON.stringify(newVisibleColumns));
                                    }}
                                  />
                                );
                              })}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </div>
                  )}
                </div>
              </div>
            </Popover>

            <Button
              variant={autoResizeEnabled ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setAutoResizeEnabled(!autoResizeEnabled)}
            >
              {autoResizeEnabled ? '✓ ' : ''}Auto-resize
            </Button>
          </div>
        </div>

        {/* Grouped Tables - Scrollable Container */}
        <div style={{
          backgroundColor: 'var(--dark-surface)',
          overflowX: 'auto',
          overflowY: 'auto',
          flex: 1,
          minWidth: 0, // Allow flex item to shrink and enable horizontal scroll
        }}>
        {Object.keys(groupedDiscrepancies).length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-12)',
            color: 'var(--gray-400)',
          }}>
            <FileWarning size={48} style={{ marginBottom: 'var(--space-4)' }} />
            <p style={{ fontSize: 'var(--text-lg)', margin: 0, fontWeight: 'var(--font-medium)' }}>
              No discrepancies found
            </p>
          </div>
        ) : (
          Object.entries(groupedDiscrepancies).map(([status, items], groupIndex) => {
            const isExpanded = expandedGroups[status] !== false;

            return (
              <React.Fragment key={status}>
                {/* Group Header */}
                <div
                  onClick={() => toggleGroup(status)}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: 'var(--dark-bg)',
                    borderTop: groupIndex > 0 ? '1px solid var(--gray-600)' : 'none',
                    transition: 'background 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    gap: '12px',
                    userSelect: 'none',
                    position: 'sticky',
                    left: 0,
                    right: 0,
                    zIndex: 1,
                    minWidth: 'fit-content',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--dark-elevated)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--dark-bg)';
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', color: 'var(--gray-400)' }}>
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </span>
                  <span
                    style={{
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: 'white',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      backgroundColor: getStatusColor(status),
                    }}
                  >
                    {status}
                  </span>
                  <span style={{ fontSize: '14px', color: 'var(--gray-400)', fontWeight: '500' }}>
                    ({items.length})
                  </span>
                </div>

                {/* Group Items - Table Component */}
                {isExpanded && (
                  <Table
                    key={`table-${status}`}
                    data={status === autoResizeGroup ? autoResizeData : items} // Use combined data for auto-resize calculations
                    dataToDisplay={items} // But only display items from this group
                    columns={columns}
                    sortable={false} // Group-specific sorting handled in useMemo
                    searchable={false} // Search handled at top level
                    resizable={true}
                    paginated={false}
                    autoResizeOnDataChange={autoResizeEnabled && status === autoResizeGroup} // Only auto-resize based on priority group
                    showAutoSizeButton={false}
                    storageKey="discrepancy-table" // Shared across all groups for consistent widths
                    loading={false}
                    emptyMessage="No items in this group"
                    onRowClick={handleRowClick}
                    onManualResize={handleManualResize} // Auto-disable auto-resize when user manually resizes
                    variant="dense"
                    stickyHeader={false}
                    style={{
                      border: 'none',
                      borderRadius: '0',
                      overflow: 'visible',
                      width: '100%',
                    }}
                  />
                )}
              </React.Fragment>
            );
          })
        )}
        </div>
      </div>

      {/* Discrepancy Modal - Route to correct modal based on status */}
      {modalOpen && (() => {
        // Determine which modal to use based on status
        const status = selectedDiscrepancy?.discrepancyStatus || selectedDiscrepancy?.status;
        const useReviewModal = !isCreating && status === 'Waiting to Review';

        if (useReviewModal) {
          // Use DiscrepancyReviewModal for "Waiting to Review" status
          return (
            <DiscrepancyReviewModal
              discrepancy={selectedDiscrepancy}
              isOpen={modalOpen}
              onClose={handleModalClose}
              onSave={handleDiscrepancySave}
              onDelete={handleDiscrepancyDelete}
            />
          );
        } else {
          // Use DiscrepancyModal for Draft, Sent, Reviewed, Resolved, and creating new
          return (
            <DiscrepancyModal
              discrepancy={selectedDiscrepancy}
              isOpen={modalOpen}
              isCreating={isCreating}
              onClose={handleModalClose}
              onSave={handleDiscrepancySave}
              onDelete={handleDiscrepancyDelete}
            />
          );
        }
      })()}

      {/* Contact Details Modal */}
      {showContactModal && selectedContact && (
        <ContactDetailsModal
          contact={selectedContact}
          isOpen={showContactModal}
          onClose={() => {
            setShowContactModal(false);
            setSelectedContact(null);
          }}
        />
      )}
    </div>
  );
};

export default DiscrepancyTable;
